import React, { useMemo, useState } from "react";
import { formatAmount, IAmount, IPosType } from "@foodi/core";
import { Pressable, StyleSheet, View, ViewStyle } from "react-native";
import { Receipt, Title13, Title16 } from "@stylesheets";
import { Colors, Spacing } from "@constants";
import moment from "moment";
import _ from "lodash";
import { I18n } from "react-redux-i18n";
import { VisibilityBlack, Download } from "@assets";
import { useDevices } from "@hooks";
import {
  BalanceScreens,
  TransactionsViewModel,
  TransactionTypes,
} from "@modules";
import { useDispatch } from "react-redux";
import { CloseXButton, ModalTemplate } from "@atomic";
import { NavigationProp } from "@react-navigation/native";
import { htmlStringToPdfDownload } from "@utils";

interface IProps {
  title: string;
  transactionDate: string;
  transactionType: string;
  type?: IPosType;
  total: IAmount;
  style?: ViewStyle;
  forwardTestID?: string;
  id: string;
  isCredit?: boolean;
  navigation: NavigationProp<any>;
  isSplittable?: boolean;
  orderAmount?: string;
  refillAmount?: string;
}

export const TransactionRow: React.FC<IProps> = React.memo(
  ({
    forwardTestID,
    title,
    transactionDate,
    total,
    id,
    navigation,
    transactionType,
    isCredit,
    isSplittable,
    orderAmount,
    refillAmount,
  }) => {
    const dispatch = useDispatch();
    const [isMobile] = useDevices();
    const styles = useMemo(() => _styles(isMobile), [isMobile]);

    const [receipt, setReceipt] = useState<string | undefined>("");

    const amount = parseFloat(total?.amount ?? "0");
    const isRecharge = !title;
    let formattedAmount = isRecharge ? "+" : "";
    formattedAmount += `${formatAmount(Math.abs(amount), "€")}`;

    const transactionVM = new TransactionsViewModel();

    const handleDetail = () => {
      navigation?.navigate(BalanceScreens.TICKET_PAGE, {
        id,
      });
    };

    const handleReceipt = async () => {
      const result = await transactionVM.getHandleReceipt(
        receipt || "",
        setReceipt,
        dispatch,
        id,
      );
      if (_.isEmpty(result))
        navigation.navigate(BalanceScreens.ERROR_RECEIPT, {
          errorType: "errorHandler.errorReceipt",
        });
    };

    const handleClose = () => {
      setReceipt(undefined);
    };

    const handlePdfReceiptDownload = async () => {
      const htmlReceipt =
        (await transactionVM.getHandleReceipt(
          "",
          (value) => value,
          dispatch,
          id,
        )) || "";
      await htmlStringToPdfDownload(
        htmlReceipt,
        `${moment(new Date(transactionDate)).format(
          "yyyy/MM/DD_HH:mm",
        )}_${I18n.t("refill.receiptCopy")}`,
        isMobile,
      );
    };

    const receiptModal = (
      <ModalTemplate
        isOpen={!!receipt}
        closeOnClickOutside
        handleClose={handleClose}
        style={styles.receiptDiv}
        customComponentCloseButton={
          <>
            <CloseXButton callback={handleClose} />
            <Pressable
              style={styles.downLoadContainerModal}
              onPress={handlePdfReceiptDownload}
            >
              <Download />
            </Pressable>
          </>
        }
      >
        {receipt && (
          <Receipt
            style={styles.receipt}
            dangerouslySetInnerHTML={{ __html: receipt }}
          />
        )}
      </ModalTemplate>
    );

    // For splittable transactions, show sub-rows for debit and refill
    if (isSplittable && orderAmount && refillAmount) {
      const formattedOrderAmount = `${formatAmount(
        Math.abs(parseFloat(orderAmount)),
        "€",
      )}`;
      const formattedRefillAmount = `${formatAmount(
        Math.abs(parseFloat(refillAmount)),
        "€",
      )}`;

      return (
        <>
          <View style={styles.splittableContainer}>
            <View style={styles.splittableMainRow}>
              <View style={styles.splittableHeader}>
                <Title16
                  style={styles.title}
                  numberOfLines={1}
                  ellipsizeMode={"tail"}
                >
                  {title}
                </Title16>
                <View style={styles.splittableMetaRow}>
                  <Title13 style={styles.date}>
                    {_.upperFirst(
                      moment(new Date(transactionDate)).format(
                        "DD/MM/yyyy  HH:mm",
                      ),
                    )}
                  </Title13>
                  <Pressable onPress={handleReceipt}>
                    <VisibilityBlack />
                  </Pressable>
                  <Pressable
                    style={styles.downLoadContainer}
                    onPress={handlePdfReceiptDownload}
                  >
                    <Download />
                  </Pressable>
                </View>
              </View>
              <View style={styles.subRowsContainer}>
                <View style={styles.splittableSubRow}>
                  <Title13 style={styles.subLabel}>
                    {_.upperFirst(I18n.t("refill.debit"))}:{" "}
                  </Title13>
                  <Title13 style={styles.debit}>
                    - {formattedOrderAmount}
                  </Title13>
                </View>
                <View style={styles.splittableSubRow}>
                  <Title13 style={styles.subLabel}>
                    {_.upperFirst(I18n.t("refill.recharge"))}:{" "}
                  </Title13>
                  <Title13 style={styles.recharge}>
                    + {formattedRefillAmount}
                  </Title13>
                </View>
              </View>
            </View>
            <View style={styles.splittableInfoContainer}>
              <Title13 style={styles.splittableInfoText}>
                {"ℹ️ "}
                {I18n.t("refill.splittableInfo")}
              </Title13>
            </View>
          </View>
          {receiptModal}
        </>
      );
    }

    return (
      <>
        {/* FS-7323: Hide transaction detail for Release 18.
        NOTE: Dont delete this code, its to be ready after Release 18.
        <Pressable
          testID={forwardTestID}
          style={styles.container}
          onPress={handleDetail}
          disabled={!title}
        > */}
        <View style={styles.container}>
          <View>
            <View style={styles.rowStyle}>
              <View style={styles.infoDiv}>
                <Title16
                  style={styles.title}
                  numberOfLines={1}
                  ellipsizeMode={"tail"}
                >
                  {transactionType === TransactionTypes.ORDER
                    ? `${
                        isCredit
                          ? _.upperFirst(I18n.t("refill.credit"))
                          : _.upperFirst(I18n.t("refill.debit"))
                      } - ` + title
                    : _.upperFirst(I18n.t("refill.recharge"))}
                </Title16>
                <View style={styles.rowStyle2}>
                  <Title13 style={styles.date}>
                    {_.upperFirst(
                      moment(new Date(transactionDate)).format(
                        "DD/MM/yyyy  HH:mm",
                      ),
                    )}
                  </Title13>
                  <Pressable onPress={handleReceipt}>
                    <VisibilityBlack />
                  </Pressable>
                  <Pressable
                    style={styles.downLoadContainer}
                    onPress={handlePdfReceiptDownload}
                  >
                    <Download />
                  </Pressable>
                </View>
              </View>
            </View>
          </View>
          <Title16
            style={[
              styles.title,
              isCredit ? [styles.recharge] : [styles.debit],
            ]}
          >
            {amount === 0 ? formattedAmount : `${isCredit ? "+" : "-"} ${formattedAmount}`}
          </Title16>
        </View>
        {receiptModal}
      </>
    );
  },
);

const _styles = (isMobile: boolean) =>
  StyleSheet.create({
    container: {
      marginTop: isMobile ? Spacing.S : Spacing.M,
      flex: 1,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    splittableContainer: {
      marginTop: isMobile ? Spacing.S : Spacing.M,
      width: "100%",
      backgroundColor: Colors.background1,
      borderRadius: 8,
      paddingHorizontal: Spacing.S,
      paddingVertical: Spacing.S,
      flexDirection: "column",
      position: "relative",
    },
    splittableMainRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    splittableHeader: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
    splittableInfoContainer: {
      marginTop: Spacing.S,
      padding: Spacing.S,
      borderRadius: 6,
      backgroundColor: "rgba(0,132,255,0.08)",
      borderWidth: 1,
      borderColor: "rgba(0,132,255,0.35)",
    },
    splittableInfoText: {
      color: Colors.navyBlue,
    },
    splittableMetaRow: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: Spacing.XS,
    },
    divider: {
      height: 1,
      backgroundColor: Colors.border1,
      marginTop: Spacing.S,
      marginBottom: Spacing.XS,
    },
    subRowsContainer: {
      alignItems: "flex-start",
    },
    splittableSubRow: {
      width: "100%",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    subLabel: {
      color: Colors.foodiBlack,
    },
    rowStyle: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    rowStyle2: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    infoDiv: {
      marginLeft: 0,
      justifyContent: "center",
      alignItems: "flex-start",
      flex: 1,
    },
    title: {
      fontWeight: "600",
    },
    date: {
      color: Colors.foodiBlack,
      marginRight: 10,
    },
    recharge: {
      color: Colors.middleGreen,
      fontWeight: "600",
    },
    debit: {
      color: Colors.redError,
      fontWeight: "600",
    },
    receiptDiv: {
      width: isMobile ? "100%" : 520,
      maxHeight: isMobile ? "100%" : "95vh",
    },
    downLoadContainer: {
      marginLeft: 10,
    },
    downLoadContainerModal: {
      position: "absolute",
      right: 60,
      top: -5,
    },
    receipt: {
      marginTop: 10,
    },
  });
